'use client';

import {
  DrawerCloseIconWrapper,
  DrawerContainer,
  DrawerContent,
  DrawerFooter,
  DrawerFooterLayout,
  DrawerHeader,
  DrawerHeaderLayout,
  DrawerOverlay,
  HeadingText,
} from './DrawerStyles';
import { MDSDrawerProps } from './types';
import { Close as CloseIcon, MDSText, toKebabCase } from '@midwest/web/base';
import { MDSButton } from '@midwest/web/button';
import * as Drawer from '@radix-ui/react-dialog';
import { colors } from '@thrivent/midwest-shared';
import { useCallback } from 'react';

/**
 ```jsx
 import { MDSDrawer } from '@midwest/web/drawer';
 ```
 * A drawer is a panel that slides over the screen when triggered. Drawers display additional content or enable small interactions related to the “parent” page.
 * The children of the drawer will be rendered inside of the drawer body. If it is a string it will be formatted with the default typography styles, otherwise any React components will be rendered.
 */
export const MDSDrawer = ({
  size = 'md',
  heading,
  open,
  setOpen,
  primaryButtonText,
  onClickPrimary,
  secondaryButtonText,
  onClickSecondary,
  onDismiss,
  zIndex = 500,
  trigger,
  children,
  id,
  dataAnalyticsModalName = heading || 'drawer',
}: MDSDrawerProps) => {
  const dismiss = () => {
    handleClick('dismiss');
  };
  const handleClick = (type: 'primary' | 'secondary' | 'dismiss') => {
    if (type === 'primary') {
      onClickPrimary && onClickPrimary();
    } else if (type === 'secondary') {
      onClickSecondary && onClickSecondary();
    }
    onDismiss && onDismiss();
    setOpen(false);
  };
  const setRef = useCallback(
    (node: {
      scrollHeight: number;
      clientHeight: number;
      tabIndex: number;
    }) => {
      if (node) {
        const hasVerticalScrollbar = node.scrollHeight > node.clientHeight;
        if (hasVerticalScrollbar) {
          node.tabIndex = 0;
        }
      }
    },
    [],
  );
  return (
    <Drawer.Root open={open} onOpenChange={setOpen}>
      {!!trigger && <Drawer.Trigger asChild>{trigger}</Drawer.Trigger>}
      <Drawer.Portal>
        <DrawerOverlay />
        <DrawerContainer
          size={size}
          $zIndex={zIndex}
          onEscapeKeyDown={dismiss}
          onInteractOutside={dismiss}
          data-analytics-name={dataAnalyticsModalName}
          aria-describedby={undefined}
        >
          <DrawerHeaderLayout>
            {!!heading && (
              <DrawerHeader>
                <HeadingText as="span">{heading}</HeadingText>
              </DrawerHeader>
            )}
            <DrawerCloseIconWrapper
              data-testid="close-button"
              onClick={() => handleClick('dismiss')}
              role="button"
              aria-label="Close drawer"
            >
              <CloseIcon fill={colors.textSecondary} />
            </DrawerCloseIconWrapper>
          </DrawerHeaderLayout>
          <DrawerContent
            ref={(node: HTMLDivElement | null) =>
              setRef(
                node as {
                  scrollHeight: number;
                  clientHeight: number;
                  tabIndex: number;
                },
              )
            }
            z-index={zIndex}
          >
            {typeof children === 'string' ? (
              <MDSText.BodyRegDefault color={colors.textSecondary}>
                {children}
              </MDSText.BodyRegDefault>
            ) : (
              children
            )}
          </DrawerContent>
          <DrawerFooterLayout>
            <DrawerFooter>
              <MDSButton
                data-analytics-id={
                  toKebabCase(id) + '-' + toKebabCase(primaryButtonText)
                }
                onClick={() => handleClick('primary')}
                data-testid="primary-button"
              >
                {primaryButtonText}
              </MDSButton>
              {!!secondaryButtonText && (
                <MDSButton
                  data-analytics-id={
                    toKebabCase(id) + '-' + toKebabCase(secondaryButtonText)
                  }
                  weight="subtle"
                  onClick={() => handleClick('secondary')}
                  data-testid="secondary-button"
                >
                  {secondaryButtonText}
                </MDSButton>
              )}
            </DrawerFooter>
          </DrawerFooterLayout>
        </DrawerContainer>
      </Drawer.Portal>
    </Drawer.Root>
  );
};
